/**
 * MandateDeliveryActions - Zustellen and Freigabe zurückziehen for a released post or portal mandate,
 * and Freigabe zurückziehen alone for a released email mandate whose send did not complete.
 *
 * A post or portal mandate is only RELEASED in the wizard (P4-S1 step 6). Until it is delivered it
 * opens no Vorgang, yet it already blocks a new prepare of its kind (409 mandate_process_open). This
 * is where the broker moves it on, from the Mandatskachel in the customer header and from the
 * Vorgänge card on the Übersicht:
 *
 *   - Zustellen, post: the broker printed and posted the document and confirms it
 *     («gedruckt und versendet», POST /mandates/:id/mark-posted). The api commits it like a send:
 *     the Vorgang opens on the post channel and the Mahnwesen starts.
 *   - Zustellen, portal: the stored notification goes out on the existing send
 *     (POST /mandates/:id/send), to the recipient the preview resolves (GET /mandates/:id/delivery),
 *     without the attachment: the document reaches the customer in the portal.
 *   - Freigabe zurückziehen: the existing cancel route deletes the released mandate and its
 *     document, so a new prepare is possible again.
 *
 * Every action asks for confirmation first. The api decides what is allowed (rule #2); the
 * permission props only hide a button the caller could not use anyway: post and the withdrawal
 * need `mandate.dispatch`, the portal send both `mandate.dispatch` and `communication.send` (the
 * api's /send enforces both).
 *
 * A withdrawal that loses the race to a delivery answers 409. The caller refetches, so the tile shows
 * the mandate as it now is, and that refetch usually unmounts this component (the mandate is sent, its
 * pending tile is gone), taking an inline error with it. So the dialog closes and the server's message
 * goes to the caller with onChanged, which shows it in a place that survives the refetch
 * (`MandateDeliveryNotice`).
 *
 * NO em/en dashes here (ESLint).
 */

import { useState } from 'react';
import { AlertCircle, Loader2, Printer, Send, Undo2, X } from 'lucide-react';
import { PORTAL_INVITATION_MARKER, PORTAL_INVITATION_PLACEHOLDER } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { ApiError, apiClient } from '../../../lib/api-client';
import type { MandateDeliveryPreviewWire, MandatePendingDeliveryWire } from '../../../lib/api-client';
import { dateTimeShortDe } from '../../../lib/format';

/** The anchor the wizard's «Offenes Mandat anzeigen» scrolls to. */
export function mandatePendingAnchorId(mandateId: string): string {
  return `mandate-pending-${mandateId}`;
}

function kindLabel(kind: 'analyse' | 'broker'): string {
  return kind === 'broker' ? 'Brokermandat' : 'Analysemandat';
}

function channelLabel(channel: 'post' | 'portal' | 'email'): string {
  return channel === 'post' ? 'Post' : channel === 'portal' ? 'Portal' : 'E-Mail';
}

type Confirm = 'deliver' | 'withdraw' | null;

/** The withdraw 409's text when the api sent none (it normally sends its own, which is shown). */
export const MANDATE_ALREADY_SENT_NOTICE = 'Das Mandat wurde inzwischen versendet.';

/**
 * The caller's notice for what MandateDeliveryActions reports with onChanged(notice): rendered by the
 * screen, not by the actions, so it outlives the refetch that removes the pending tile. Stays until
 * dismissed.
 */
export function MandateDeliveryNotice({ message, onDismiss }: { message: string | null; onDismiss: () => void }) {
  if (message === null) return null;
  return (
    <div
      role="alert"
      data-testid="mandate-delivery-notice"
      className="fixed bottom-4 left-1/2 z-50 flex max-w-md -translate-x-1/2 items-start gap-2 rounded-lg border border-destructive/40 bg-card px-4 py-2.5 text-sm text-destructive shadow-md"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span className="flex-1">{message}</span>
      <button
        type="button"
        aria-label="Hinweis schliessen"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
        onClick={onDismiss}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export function MandateDeliveryActions({
  kind,
  pending,
  canDispatch,
  canSend,
  onChanged,
  layout = 'row',
}: {
  kind: 'analyse' | 'broker';
  pending: MandatePendingDeliveryWire;
  /** `mandate.dispatch`: post Zustellen and Freigabe zurückziehen. */
  canDispatch: boolean;
  /** `communication.send`: portal Zustellen, together with `canDispatch` (it mails the notification). */
  canSend: boolean;
  /**
   * The mandate moved (delivered, withdrawn, or found already sent on a 409): the caller refetches.
   * `notice` is set on the 409: a message the caller shows itself, because the refetch can unmount
   * this component and with it any error shown here.
   */
  onChanged: (notice?: string) => void;
  /** 'tile' adds the label pill for the customer header; 'row' is the buttons alone. */
  layout?: 'tile' | 'row';
}) {
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MandateDeliveryPreviewWire | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const isPost = pending.channel === 'post';
  // Portal: the delivery preview needs mandate.dispatch, the send communication.send. A released
  // email mandate (its send did not complete) offers only the withdrawal; it is sent from the wizard.
  const canDeliver = pending.channel === 'email' ? false : isPost ? canDispatch : canSend && canDispatch;
  const label = kindLabel(kind);

  const close = () => {
    if (busy) return;
    setConfirm(null);
    setError(null);
  };

  const openDeliver = () => {
    setConfirm('deliver');
    setError(null);
    if (isPost) return;
    // Portal: the recipient and the stored notification come from the api.
    setPreview(null);
    setBusy(true);
    apiClient.mandateWizard
      .deliveryPreview(pending.mandateId)
      .then((p) => {
        setPreview(p);
        setTo(p.to.join(', '));
        setSubject(p.subject ?? '');
        setBody(p.body ?? '');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Die Zustellung konnte nicht vorbereitet werden.');
      })
      .finally(() => setBusy(false));
  };

  const run = async (action: () => Promise<unknown>, fallback: string, refetchOnConflict = false) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setBusy(false);
      setConfirm(null);
      onChanged();
    } catch (err: unknown) {
      setBusy(false);
      // A 409 means the mandate moved under us (delivered meanwhile): the tile is stale. The refetch
      // usually unmounts this component, so the message goes to the caller, not into the dialog.
      if (refetchOnConflict && err instanceof ApiError && err.status === 409) {
        setConfirm(null);
        setError(null);
        onChanged(err.message.trim() !== '' ? err.message : MANDATE_ALREADY_SENT_NOTICE);
        return;
      }
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const deliver = () => {
    if (isPost) {
      void run(() => apiClient.mandateWizard.markPosted(pending.mandateId), 'Die Zustellung ist fehlgeschlagen.');
      return;
    }
    const recipients = to
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    if (recipients.length === 0) {
      setError('Bitte erfassen Sie mindestens eine Empfängeradresse.');
      return;
    }
    if (subject.trim() === '' || body.trim() === '') {
      setError('Bitte erfassen Sie Betreff und Nachricht der Benachrichtigung.');
      return;
    }
    void run(
      () =>
        apiClient.mandateWizard.send(pending.mandateId, {
          to: recipients,
          subject,
          body,
          // The document reaches the customer in the portal; the mail is the notification only.
          includeAttachment: false,
        }),
      'Die Zustellung ist fehlgeschlagen.',
    );
  };

  // The post Brokermandat's portal Begleitbrief: printed with the mandate, before «gedruckt und versendet».
  const hasBrief = isPost && pending.portalBrief === true;
  const openBrief = () => {
    setError(null);
    apiClient.mandateWizard
      .portalBriefBlob(pending.mandateId)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Der Begleitbrief konnte nicht geöffnet werden.');
      });
  };

  const withdraw = () => {
    void run(
      () => apiClient.mandateWizard.cancel(pending.mandateId),
      'Die Freigabe konnte nicht zurückgezogen werden.',
      true,
    );
  };

  if (!canDeliver && !canDispatch && layout === 'row') return null;

  const storedText = preview !== null && preview.subject !== null && preview.body !== null;
  const when = dateTimeShortDe(pending.releasedAt);
  const tileText = [`${label} freigegeben`, channelLabel(pending.channel), ...(when !== '' ? [when] : [])].join(' · ');

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid={layout === 'tile' ? mandatePendingAnchorId(pending.mandateId) : undefined}
      id={layout === 'tile' ? mandatePendingAnchorId(pending.mandateId) : undefined}
    >
      {layout === 'tile' && (
        <span className="rounded-md bg-sky-100 px-2 py-1 text-xs font-medium text-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
          {tileText}
        </span>
      )}
      {hasBrief && canDispatch && (
        <Button size="sm" variant="outline" onClick={openBrief}>
          <Printer className="size-3.5" />
          Begleitbrief Kundenportal
        </Button>
      )}
      {canDeliver && (
        <Button size="sm" variant="outline" onClick={openDeliver}>
          <Send className="size-3.5" />
          Zustellen
        </Button>
      )}
      {canDispatch && (
        <Button size="sm" variant="ghost" onClick={() => { setConfirm('withdraw'); setError(null); }}>
          <Undo2 className="size-3.5" />
          Freigabe zurückziehen
        </Button>
      )}

      {confirm !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={confirm === 'deliver' ? `${label} zustellen` : 'Freigabe zurückziehen'}
        >
          <div className="mx-4 flex w-full max-w-lg flex-col gap-3 rounded-lg border bg-card p-5 text-sm shadow-lg">
            <h2 className="text-sm font-semibold">
              {confirm === 'deliver' ? `${label} zustellen (${channelLabel(pending.channel)})` : 'Freigabe zurückziehen'}
            </h2>
            {pending.processNumber && (
              <p className="text-xs text-muted-foreground">Vorgangs-ID {pending.processNumber}</p>
            )}

            {confirm === 'withdraw' && (
              <p>
                Die Freigabe wird zurückgezogen und das erstellte Dokument gelöscht. Dem Kunden wurde noch
                nichts zugestellt. Danach kann das Mandat neu vorbereitet werden.
              </p>
            )}

            {confirm === 'deliver' && isPost && (
              <p>
                Bitte bestätigen Sie, dass das Dokument gedruckt und per Post versendet wurde. Der Vorgang
                wird eröffnet und das Mahnwesen startet ab heute.
              </p>
            )}
            {confirm === 'deliver' && hasBrief && (
              <p className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-3 py-2 text-xs" data-testid="portal-brief-note">
                Der Begleitbrief mit dem persönlichen Portal-Link gehört in denselben Umschlag.
                <Button size="sm" variant="ghost" onClick={openBrief}>
                  <Printer className="size-3.5" />
                  Begleitbrief drucken
                </Button>
              </p>
            )}

            {confirm === 'deliver' && !isPost && preview === null && busy && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Zustellung wird vorbereitet …
              </p>
            )}

            {confirm === 'deliver' && !isPost && preview !== null && (
              <div className="flex flex-col gap-2">
                <p>
                  Das Dokument wird ins Kundenportal gestellt und der Kunde per E-Mail benachrichtigt. Der
                  Vorgang wird eröffnet und das Mahnwesen startet ab heute.
                </p>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">An</span>
                  <input
                    className="rounded border bg-background px-2 py-1 text-sm"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    disabled={busy}
                  />
                </label>
                {storedText ? (
                  <div className="rounded border bg-muted/40 px-3 py-2 text-xs">
                    <p className="font-medium">{preview.subject}</p>
                    <p className="mt-1 whitespace-pre-wrap">
                      {(preview.body ?? '').split(PORTAL_INVITATION_PLACEHOLDER).join(PORTAL_INVITATION_MARKER)}
                    </p>
                  </div>
                ) : (
                  <>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium">Betreff</span>
                      <input
                        className="rounded border bg-background px-2 py-1 text-sm"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={busy}
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium">Nachricht</span>
                      <textarea
                        className="min-h-24 rounded border bg-background px-2 py-1 text-sm"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        disabled={busy}
                      />
                    </label>
                  </>
                )}
              </div>
            )}

            {error !== null && (
              <p className="flex items-start gap-2 text-xs text-destructive">
                <AlertCircle className="size-4 shrink-0" />
                {error}
              </p>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={close} disabled={busy}>
                Abbrechen
              </Button>
              {confirm === 'withdraw' ? (
                <Button size="sm" variant="destructive" onClick={withdraw} disabled={busy}>
                  Freigabe zurückziehen
                </Button>
              ) : (
                <Button size="sm" onClick={deliver} disabled={busy || (!isPost && preview === null)}>
                  {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                  {isPost ? 'Gedruckt und versendet' : 'Ins Portal zustellen'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
